import * as Sentry from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import {
  AirtableStatus,
  IS_PRO,
  TYPESENSE_COLLECTION,
} from 'gatsby-theme-hem-store/src/constants';
import { useEffect, useState } from 'react';

import { useShowPreOrderVariants } from '@/hooks/useShowPreOrderVariants';

export interface Variants {
  facet_counts?: null[] | null;
  found: number;
  hits?: VariantsHitsEntity[] | null;
  out_of: number;
  page: number;
  request_params: VariantsRequestParams;
  search_cutoff: boolean;
  search_time_ms: number;
}
export interface VariantsHitsEntity {
  document: VariantsDocument;
  highlight: VariantsHighlight;
  highlights?: null[] | null;
}
export interface VariantsDocument {
  approxLaunchDate: number;
  categories?: {
    lvl0?: string[] | null;
    lvl1?: string[] | null;
  };
  certifications?: string[] | null;
  colors?: null[] | null;
  compareAtPrice: number;
  designer: string;
  hoverImageUrl: string;
  id: string;
  imageUrl: string;
  inStock: boolean;
  materials?: {
    lvl0?: string[] | null;
    lvl1?: string[] | null;
    lvl2?: string[] | null;
  };
  onSale: boolean;
  price: number;
  priority: number;
  productColors?: string[] | null;
  productName: string;
  shipmentDate: number;
  shopifyId: string;
  sku: string;
  slug: string;
  status: string;
  tags?: null[] | null;
  type: string;
  variantConfiguration: string;
  variantName: string;
}
export interface VariantsHighlight {
  productName?: {
    snippet: string;
  };
  sku?: {
    snippet: string;
  };
  tags?: {
    snippet: string;
  };
  variantName?: {
    snippet: string;
  };
}
export interface VariantsRequestParams {
  collection_name: string;
  per_page: number;
  q: string;
}

export interface Suggestions {
  facet_counts?: null[] | null;
  found: number;
  hits?: SuggestionsHitsEntity[] | null;
  out_of: number;
  page: number;
  request_params: SuggestionsRequestParams;
  search_cutoff: boolean;
  search_time_ms: number;
}
export interface SuggestionsHitsEntity {
  document: SuggestionsDocument;
  highlight: SuggestionsHighlight;
  highlights?: null[] | null;
}
export interface SuggestionsDocument {
  count: number;
  id: string;
  q: string;
}
export interface SuggestionsHighlight {
  q?: {
    snippet: string;
  };
}
export interface SuggestionsRequestParams {
  collection_name: string;
  per_page: number;
  q: string;
}

export interface Designers {
  facet_counts?: null[] | null;
  found: number;
  hits?: DesignersHitsEntity[] | null;
  out_of: number;
  page: number;
  request_params: DesignersRequestParams;
  search_cutoff: boolean;
  search_time_ms: number;
}
export interface DesignersHitsEntity {
  document: DesignersDocument;
  highlight: DesignersHighlight;
  highlights?: null[] | null;
}
export interface DesignersDocument {
  name: string;
  description: string;
  image: string;
  id: string;
  slug: string;
}
export interface DesignersHighlight {
  name?: {
    snippet: string;
    matched_tokens: string[];
  };
  description?: {
    snippet: string;
    matched_tokens: string[];
  };
}
export interface DesignersRequestParams {
  collection_name: string;
  per_page: number;
  q: string;
}

export const useAutocomplete = ({ query = '*' }) => {
  const showPreOrderVariants = useShowPreOrderVariants();
  const [{ suggestions, variants, materials, designers }, setHits] = useState<{
    suggestions?: Suggestions;
    variants?: Variants;
    materials?: Variants;
    designers?: Designers;
  }>({
    suggestions: undefined,
    variants: undefined,
    materials: undefined,
    designers: undefined,
  });

  const q = query.trim() || '*';

  const { data, status } = useQuery({
    queryKey: ['autocomplete', query],
    queryFn: async () => {
      try {
        const url = `https://${process.env.GATSBY_TYPESENSE_HOST}:${process.env.GATSBY_TYPESENSE_PORT}/multi_search`;
        const options = {
          method: 'POST',
          headers: {
            'X-Typesense-Api-Key': process.env.GATSBY_TYPESENSE_SEARCH_KEY,
          },
          body: JSON.stringify({
            searches: [
              // Variants
              {
                collection: TYPESENSE_COLLECTION,
                q,
                sort_by: '_eval(inStock:true):desc,priority:desc',
                query_by:
                  'sku,productName,variantName,categories.lvl1,designer,tags',
                query_by_weights: '5,5,3,3,1,2',
                num_typos: '0,2,2,2,2,2',
                limit: 10,
                exclude_fields: 'embedding',
                filter_by: [
                  'tags:!=[secret,algolia-ignore]',
                  'type:=variant',
                  showPreOrderVariants
                    ? null
                    : `status:!=${AirtableStatus.PRE_ORDER}`,
                ]
                  .filter(Boolean)
                  .join(' && '),
              },
              // Materials
              {
                collection: TYPESENSE_COLLECTION,
                q,
                sort_by: '_text_match:desc',
                query_by: 'sku,productName,variantName,categories.lvl2,tags',
                query_by_weights: '5,5,3,1,2',
                num_typos: '0,2,2,2,2',
                limit: 6,
                exclude_fields: 'embedding',
                filter_by: [
                  'tags:!=[secret,algolia-ignore]',
                  'type:=material',
                  showPreOrderVariants
                    ? null
                    : `status:!=${AirtableStatus.PRE_ORDER}`,
                ]
                  .filter(Boolean)
                  .join(' && '),
              },
              // Designers
              {
                collection: `Designers ${IS_PRO ? 'B2B' : 'B2C'}`,
                q,
                limit: 3,
                sort_by: '_text_match:desc',
                query_by: 'name,description,products',
                query_by_weights: '5,1,3',
              },
              // Suggestions
              {
                collection: 'search_queries',
                q,
                limit: '10',
                sort_by: 'count:desc',
                query_by: 'q',
                filter_by: ['count:>4', 'count:<2000']
                  .filter(Boolean)
                  .join(' && '),
              },
            ],
          }),
        };

        const response = await fetch(url, options);

        if (!response.ok) {
          throw new Error('Failed to fetch autocomplete results');
        }

        const json = await response.json();

        return json;
      } catch (error) {
        console.error(
          'An error occurred while fetching autocomplete results',
          error,
        );

        Sentry.captureException(error);
        throw error;
      }
    },
  });

  useEffect(() => {
    if (status === 'success') {
      setHits({
        variants: data.results[0],
        materials: data.results[1],
        designers: data.results[2],
        suggestions: data.results[3],
      });
    }
  }, [data, status]);

  return {
    designers,
    suggestions,
    variants,
    materials,
  };
};
